/**
 * LLM-assisted tool selection advisor
 *
 * Given the current session context and the deterministic compatibility results,
 * generates a plain-language recommendation explaining which prioritization method
 * best fits the session. The deterministic evaluation still gates eligibility —
 * this layer only adds "why" explanations and ranked guidance among compatible tools.
 *
 * Registry: tool_advisor — embedded advisor. Transport: callFeatureLlm.
 * Strictly advisory — the user selects the final tool. AI never sets selectedToolId.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const TOOL_ADVISOR_SYSTEM_PROMPT =
  'You recommend the best prioritization method for a team based on their session context. Respond with valid JSON only.';

export interface ToolOption {
  toolId: string;
  name: string;
  description?: string;
  /** Deterministic compatibility status */
  status: 'compatible' | 'compatible_with_warnings' | 'incompatible';
  warnings: string[];
}

export interface SessionContext {
  itemCount: number;
  criteriaCount: number;
  participantCount: number;
  valuesSufficient: boolean;
  /** Session intent, e.g. rank_priorities, select_attributes */
  intent?: string;
}

export interface ToolRecommendation {
  toolId: string;
  toolName: string;
  headline: string;
  rationale: string;
  tradeoffs?: string;
  rank: number;
}

export interface ToolAdvisorResult {
  topPick: ToolRecommendation;
  alternatives: ToolRecommendation[];
  overallGuidance: string;
}

/**
 * Ask the LLM to recommend and rank the compatible tools for a given session.
 * Returns a structured recommendation with rationale per tool.
 */
export async function adviseToolSelection(
  tools: ToolOption[],
  context: SessionContext
): Promise<ToolAdvisorResult | null> {
  const compatible = tools.filter((t) => t.status !== 'incompatible');
  if (!isAiCapabilityEnabled('tool_advisor') || compatible.length === 0) return null;

  try {
    const content = await callFeatureLlm({
      userPrompt: buildAdvisorPrompt(compatible, context),
      systemPrompt: TOOL_ADVISOR_SYSTEM_PROMPT,
      conversationId: 'tool-advisor-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 800,
    });
    if (!content) return null;
    return parseAdvisorResponse(content, compatible);
  } catch (error) {
    console.warn('LLM tool advisor failed:', error);
    return null;
  }
}

function buildAdvisorPrompt(tools: ToolOption[], context: SessionContext): string {
  const toolList = tools
    .map(
      (t) =>
        `- ${t.name} (id="${t.toolId}", status="${t.status}"${t.description ? `, description="${t.description}"` : ''}${t.warnings.length > 0 ? `, warnings: ${t.warnings.join('; ')}` : ''})`
    )
    .join('\n');

  const intentLabel = context.intent ? ` The session intent is "${context.intent}".` : '';

  return `You are a prioritization facilitator helping a team choose the right method for their session.${intentLabel}

Session facts:
- Items to prioritize: ${context.itemCount}
- Scoring criteria selected: ${context.criteriaCount}
- Participants: ${context.participantCount}
- All items have sufficient values for scoring: ${context.valuesSufficient ? 'Yes' : 'No'}

Available methods (already validated as technically compatible):
${toolList}

Your task: Rank the available methods from best to worst fit for this session. For each, provide:
- A short headline (one sentence, specific to this context)
- A rationale (2-3 sentences explaining fit)
- Key tradeoffs to consider (optional, 1 sentence)

Return JSON only, no markdown or extra text:
{
  "recommendations": [
    {
      "toolId": "exact id from the list",
      "toolName": "name from the list",
      "headline": "One-line specific recommendation",
      "rationale": "2-3 sentences on why this fits",
      "tradeoffs": "Optional 1 sentence on tradeoffs",
      "rank": 1
    }
  ],
  "overallGuidance": "1-2 sentences of overall advice for this team"
}

Order by rank (1 = strongest recommendation). Include all compatible methods.`;
}

function parseAdvisorResponse(
  content: string,
  tools: ToolOption[]
): ToolAdvisorResult | null {
  const validIds = new Set(tools.map((t) => t.toolId));

  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);
    const rawRecs = Array.isArray(parsed.recommendations) ? parsed.recommendations : [];

    const recommendations: ToolRecommendation[] = rawRecs
      .filter(
        (r: unknown) =>
          r &&
          typeof r === 'object' &&
          typeof (r as { toolId?: unknown }).toolId === 'string' &&
          validIds.has((r as { toolId: string }).toolId)
      )
      .map((r: Record<string, unknown>, idx: number) => ({
        toolId: r.toolId as string,
        toolName: (r.toolName as string) ?? (tools.find((t) => t.toolId === r.toolId)?.name ?? (r.toolId as string)),
        headline: typeof r.headline === 'string' ? r.headline.trim() : '',
        rationale: typeof r.rationale === 'string' ? r.rationale.trim() : '',
        tradeoffs: typeof r.tradeoffs === 'string' ? r.tradeoffs.trim() : undefined,
        rank: typeof r.rank === 'number' ? r.rank : idx + 1,
      }))
      .sort((a: ToolRecommendation, b: ToolRecommendation) => a.rank - b.rank);

    if (recommendations.length === 0) return null;

    return {
      topPick: recommendations[0],
      alternatives: recommendations.slice(1),
      overallGuidance:
        typeof parsed.overallGuidance === 'string' ? parsed.overallGuidance.trim() : '',
    };
  } catch (e) {
    console.warn('Parse tool advisor response failed:', e);
    return null;
  }
}
